import { useEffect, useMemo, useState } from 'react'

import { Select, SelectProps, Switch } from '@/components'
import { WarrantyStatusEnum } from '@/enums'
import { useQuery } from '@/hooks'
import { WarrantyStatus } from '@/models'
import { FormChangeEvent, FormChangeEventHandler, WarrantiesSummaryDataType } from '@/types'

interface WarrantyTypeDropdownProps extends Omit<SelectProps, 'options'> {
    onChange: FormChangeEventHandler
    value: string[]
    name: string
    summary: WarrantiesSummaryDataType
}

export const WarrantyStatusFilter = ({
    summary,
    ...props
}: WarrantyTypeDropdownProps) => {
    const query = useQuery()
    const [showInactiveOptions, setShowInactiveOptions] = useState(false)
    const handleChange = (e: FormChangeEvent) => {
        if (e.target.value === 'toggle') {
            setShowInactiveOptions(value => !value)
            return
        }
        const value = (e.target.value as WarrantyStatusEnum[])
            .filter(item => summary.warranty_statuses[item] || showInactiveOptions)
        props.onChange({
            target: {
                name: props.name,
                value,
                displayValue: value.map(item => WarrantyStatus.find(item as WarrantyStatusEnum).title)
            }
        })
    }
    const getStatusCount = (item: WarrantyStatus) =>
        (summary.warranty_statuses[item.key] || 0).format()

    const options = useMemo(() => [
        {
            value: 'toggle',
            title: <div className="flex w-full gap-4 items-center justify-between font-body text-gray-900 text-sm">
                Show Inactive Warranty Statuses
                <Switch
                    name="toggle"
                    input-size="sm"
                    checked={showInactiveOptions}
                    value="toggle"
                    onChange={() => {}}
                    onClick={() => {}}
                />
            </div>
        },
        {
            value: '',
            title: 'Any Warranty Status'
        },
        ...WarrantyStatus.all
            .filter(item =>
                !item.isUnknown &&
                (summary.warranty_statuses[item.key] || showInactiveOptions))
            .map(item => ({
                value: item.key,
                title: <div className="flex gap-4 items-center justify-between font-body text-gray-900 text-sm">
                    <div className="flex items-center gap-1 text-sm">
                        {item.icon}
                        {item.title}
                    </div>
                    {getStatusCount(item)}
                </div>
            }))]
    , [props.value, summary.warranty_statuses, showInactiveOptions])

    useEffect(() => {
        const isQueryEmpty = Object.keys(query).length === 0
        if (isQueryEmpty) {
            props.onChange({
                target: {
                    name: props.name,
                    value: WarrantyStatus.defaultStatuses,
                    displayValue:  WarrantyStatus.defaultStatuses
                }
            })
        } else if (props.value.length) {
            props.onChange({
                target: {
                    name: props.name,
                    value: props.value,
                    displayValue: props.value.map(item => WarrantyStatus.find(item as WarrantyStatusEnum).title)
                }
            })
        }
    }, [])

    return <Select
        {...props}
        multiple
        onChange={handleChange}
        options={options}
    />
}
